import React from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'
import PinataLink from '../../../internal/PinataLink'
import styles from './styles'

/**
 * Renders a table row with optional link functionality.
 * It maps over its children, and if a child is clickable and a href is provided, it wraps the child in a Link provided via asLink.
 * Otherwise, it just renders the child in a table cell.
 *
 * @param {Object} props - The properties that define the TableRow component.
 * @param {string} props.className - The CSS class name of the TableRow.
 * @param {ReactNode|ReactNode[]} props.children - The child or children of the TableRow.
 * @param {string} props.href - The href for the Link. If provided, clickable children will be wrapped in a Link.
 * @param {string} props.target - The target for the Link. Only '_blank' is accepted.
 * @param {string} props.rel - The rel for the Link.
 * @param {function} props.onLinkClick - The function to be called when the PinataLink is clicked.
 * @param {function|Object} props.asLink - The routing component or object to be used when needed - eg react-router-dom Link.
 * @returns {ReactElement} The TableRow component.
 */
const TableRow = ({ className, children, href, target, rel, onLinkClick, asLink }) => (
  <tr className={cx(styles.row, className)}>
    {React.Children.map(children, child => {
      // Ignore null children
      if (!child) return null

      // Make sure to wrap only children that have been marked as clickable
      // eslint-disable-next-line eqeqeq
      const clickable = child.props.clickable != undefined ? child.props.clickable : true
      const newChild = React.cloneElement(child, { clickable: undefined })

      if (href && clickable) {
        return (
          <td className={styles.cellLink}>
            <PinataLink
              className={cx(styles.cell, styles.link)}
              to={href}
              target={target}
              rel={rel}
              tabIndex="-1"
              onClick={onLinkClick}
              as={asLink}>
              {newChild}
            </PinataLink>
          </td>
        )
      }

      return <td className={styles.cell}>{newChild}</td>
    })}
  </tr>
)

TableRow.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  href: PropTypes.string,
  target: PropTypes.oneOf(['_blank']),
  rel: PropTypes.string,
  onLinkClick: PropTypes.func,
  asLink: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
}

export default TableRow
